/**
 * This file contains the default value adaptor functions
 * for different fields with custom value setting logic
 */
import { ControlInput, FormValues, GravityField, iChoice } from "../types";
import { isValid, parseISO } from "date-fns";
import {
    convertDateToString,
    convertStringToDate
} from "../../date-field/DateV2";

type AdaptorOptions = {
    values: FormValues;
    field: GravityField;
    multiInput?: boolean;
};

/**
 * Date field default value adaptor
 */
const dateAdaptor = ({ values, field }: AdaptorOptions) => {
    const dateString = convertDateToString(
        convertStringToDate(field.defaultValue)
    );
    const parsedDate = parseISO(dateString);
    return field.defaultValue && isValid(parsedDate)
        ? {
              ...values,
              [`input_${field.id}`]: dateString
          }
        : values;
};

/**
 * Checkbox field default value adaptor
 */
const checkboxAdaptor = ({ values, field }: AdaptorOptions) => {
    const selectedValues = field.choices.reduce(
        (selectedChoices:any, choice: iChoice, i: number) => {
            return {
                ...selectedChoices,
                ...(choice.isSelected && field.inputs
                    && {[field.inputs[i].id]: choice.value})
            };
        }, {}
    );
    return {
        ...values,
        ...(Object.keys(selectedValues).length
            ? { [`input_${field.id}`]: selectedValues }
            : {})
    };
};

/**
 * Radio field default value adaptor
 */
const radioAdaptor = ({ values, field, multiInput }: AdaptorOptions) => {
    const selectedValue = field.choices.find(
        (choice: iChoice) => choice.isSelected
    );
    return {
        ...values,
        ...(selectedValue
            ? {
                  [multiInput ? field.id : `input_${field.id}`]: selectedValue.value
              }
            : {})
    };
};

/**
 * Select field default value adaptor
 */
const selectAdaptor = ({ values, field }: AdaptorOptions) => {
    const selectedValues = field.choices.filter(
        (choice: iChoice) => choice.isSelected === true
    );
    return {
        ...values,
        ...{
            ...((selectedValues.length === 0 || selectedValues.length > 1) &&
                {}),
            ...(selectedValues.length === 1 && {
                [`input_${field.id}`]: {
                    label: selectedValues[0].text,
                    value: selectedValues[0].value
                }
            })
        }
    };
};

/**
 * Multiselect field default value adaptor
 */
const multiSelectAdaptor = ({ values, field }: AdaptorOptions) => {
    const selectedValues = field.choices.filter(
        (choice: iChoice) => choice.isSelected === true
    );
    return {
        ...values,
        ...{
            ...(selectedValues.length === 0 && {}),
            ...(selectedValues.length >= 1 && {
                [`input_${field.id}`]: selectedValues.map(
                    (selectValue: iChoice) => ({
                        label: selectValue.text,
                        value: selectValue.value
                    })
                )
            })
        }
    };
};

/**
 * Single input fields
 */
const singleInputAdaptor = ({ values, field }: AdaptorOptions) => {
    return field.defaultValue ||
        (field.defaultValue === "" &&
            field.cssClass &&
            field.cssClass.includes("honeypot"))
        ? {
              ...values,
              [`input_${field.id}`]: field.defaultValue
          }
        : values;
};

/**
 * Multi input fields
 */
const getDefaultValues = (inputs: ControlInput[]) =>
    inputs.reduce(
        (v, i) => {
            if (i.choices) {
                return radioAdaptor({
                    values: v,
                    field: { type: "radio" , ...i },
                    multiInput: true
                });
            } else if (i.defaultValue) {
                return { ...v, [i.id]: i.defaultValue };
            } else {
                return v;
            }
        },
        {}
    );

const multiInputAdaptor = ({ values, field }: AdaptorOptions) => {
    const defaultValue = getDefaultValues(field.inputs || []);
    return {
        ...values,
        ...(Object.keys(defaultValue).length
            ? { [`input_${field.id}`]: defaultValue }
            : {})
    };
};

export {
    selectAdaptor,
    multiSelectAdaptor,
    dateAdaptor,
    checkboxAdaptor,
    radioAdaptor,
    singleInputAdaptor,
    multiInputAdaptor
};
